<template>
  <BaseCenteredModal
    v-model:is-modal-open="localIsModalOpen"
    size="huge"
    :click-outside-close="false"
    :close-on-escape="true"
    :title="$t('global.disponibility')"
    @close="$emit('close')"
  >
    <template #body>
      <div
        v-if="originalRequest?.[page]"
        class="flex min-h-[8rem] w-full flex-col space-y-5"
      >
        <div
          v-if="partialHandlingNames"
          class="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-900"
        >
          {{ $t('global.stock_modal_partial_hint', partialHandlingNames) }}
        </div>
        <div class="h-[80vh]">
          <AvailableItem
            :key="`${page}-${localOriginalRequest[page]?.id}`"
            v-model:original="localOriginalRequest[page]"
            v-model:new-orders="localNewOrders"
            :page="page"
            :all-originals="localOriginalRequest"
            :supplier-with-orders="supplierWithOrders"
            :default-delivery="defaultDelivery"
            :default-supplier="defaultSupplier"
            :is-flowers-category="isFlowersCategory"
            :stock-allocation-disabled="stockAllocationDisabled"
          />
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex w-full flex-row">
        <div class="flex flex-1">
          <ItemsPagination
            v-model:original-request="localOriginalRequest"
            v-model:page="internalPage"
          />
        </div>
        <div class="shrink-O flex flex-row gap-2">
          <BaseButton
            :title="$t('button.cancel')"
            color="warning"
            @click="localIsModalOpen = false"
          />
          <BaseButton
            :title="$t('button.save')"
            :disabled="cannotSubmit || submitting"
            :display-loader="submitting"
            @click="submit"
          />
        </div>
      </div>
    </template>
  </BaseCenteredModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'
import AvailableItem from '@c/Stock/Component/AvailableItem.vue'
import ItemsPagination from '@c/Stock/Component/ItemsPagination.vue'
import { getStockablePageIndexes } from '@u/helpers/stockOriginalHelpers'

const SUBMIT_SAFETY_TIMEOUT_MS = 15000

export default {
  name: 'AvailableLocationStockModal',
  components: {
    ItemsPagination,
    AvailableItem,
    BaseCenteredModal,
    BaseButton
  },
  provide() {
    return {
      draftOrdersBySupplier: this.draftOrdersBySupplier,
      ensureDraftOrdersFetched: this.ensureDraftOrdersFetched,
      addTempDraftOrder: this.addTempDraftOrder,
      removeTempDraftOrder: this.removeTempDraftOrder
    }
  },
  props: {
    isModalOpen: {
      type: Boolean,
      required: true
    },
    updateItem: {
      type: Boolean,
      required: false,
      default: false
    },
    originalRequest: {
      type: Array,
      required: false,
      default: () => []
    },
    newOrders: {
      type: Array,
      required: false,
      default: () => []
    },
    supplierWithOrders: {
      type: Array,
      required: false,
      default: () => []
    },
    locations: {
      type: Array,
      required: true
    },
    defaultDelivery: {
      type: Object,
      required: false,
      default: () => null
    },
    defaultSupplier: {
      type: Object,
      required: false,
      default: () => null
    },
    isFlowersCategory: {
      type: Boolean,
      required: false,
      default: false
    },
    stockAllocationDisabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: [
    'close',
    'update:is-modal-open',
    'update:original-request',
    'selected-items',
    'update-item-dossier',
    'update:new-orders'
  ],
  data() {
    return {
      internalPage: 0,
      draftOrdersBySupplier: {},
      draftOrdersGeneration: 0,
      submitting: false,
      submitResetTimeoutId: null
    }
  },
  computed: {
    ...mapGetters({
      dossier: 'dossier/getDossier',
      invoice: 'invoicing/getInvoice'
    }),
    localDossier() {
      return this.$route.name === 'invoice_info_route'
        ? this.invoice?.dossier
        : this.dossier
    },
    localIsModalOpen: {
      get() {
        return this.isModalOpen
      },
      set(value) {
        this.$emit('update:is-modal-open', value)
      }
    },
    localOriginalRequest: {
      get() {
        return this.originalRequest
      },
      set(value) {
        this.$emit('update:original-request', value)
      }
    },
    localNewOrders: {
      get() {
        return this.newOrders
      },
      set(value) {
        this.$emit('update:new-orders', value)
      }
    },
    page: {
      get() {
        if (this.internalPage !== 0) return this.internalPage
        const stockableIds = getStockablePageIndexes(this.originalRequest)
        return stockableIds.length ? stockableIds[0] : 0
      },
      set(value) {
        this.internalPage = value
      }
    },
    // Quand l'article ajouté (ou certains de ses composants) n'a rien à gérer ici
    // (commande manuelle / sans commande), on explique pourquoi la fenêtre ne
    // liste que certains éléments.
    partialHandlingNames() {
      const entries = (this.originalRequest ?? []).filter(Boolean)
      const nameOf = (entry) =>
        entry.item?.item_name ?? entry.item_name ?? entry.wiki_name
      const joinNames = (list) => list.map(nameOf).filter(Boolean).join(', ')
      const added = entries.filter((entry) => !entry.component_parent_id)
      const handled = entries.filter((entry) => entry.item)
      const others = entries.filter((entry) => !entry.item)
      if (!added.length || !handled.length || !others.length) return null
      const names = {
        added: joinNames(added),
        handled: joinNames(handled),
        others: joinNames(others)
      }
      // Sans nom à afficher, le message serait rendu avec des blancs
      return names.added && names.handled && names.others ? names : null
    },
    cannotSubmit() {
      return (this.originalRequest ?? []).some((request) => {
        if (!request?.item) {
          return false
        }
        return (
          request.item.stockable &&
          request.quantity >
            Object.values(request.stock)
              .map((item) => item.quantity)
              .filter((n) => parseInt(n) === n)
              .reduce((a, b) => a + b, 0) +
              request.order
        )
      })
    }
  },
  watch: {
    isModalOpen(newValue) {
      this.resetSubmitting()
      if (!newValue) {
        this.draftOrdersGeneration++
        Object.keys(this.draftOrdersBySupplier).forEach((key) => {
          delete this.draftOrdersBySupplier[key]
        })
        // Purge les brouillons de BDC temporaires ("temp-") abandonnés sans
        // avoir été soumis, pour qu'ils ne réapparaissent pas comme
        // sélectionnables lors d'une prochaine session de sélection de stock.
        if (this.localNewOrders.length) {
          this.localNewOrders = []
        }
        // Sans ce reset, un "Annuler" laissait originalRequest peuplé côté
        // parent : un save ultérieur sans rapport (handleUpdateItemDossier)
        // le retrouvait toujours "truthy" et renvoyait silencieusement au
        // back l'allocation de stock proposée puis abandonnée, au lieu de
        // repartir d'une décision fraîche (et rouvrir cette modale si le
        // back en a de nouveau besoin). On vide en tableau (pas null) : le
        // footer (ItemsPagination, cannotSubmit) reste monté pendant la
        // transition de fermeture et suppose toujours un Array.
        if (this.localOriginalRequest?.length) {
          this.localOriginalRequest = []
        }
        return
      }
      const stockableIds = getStockablePageIndexes(this.originalRequest)
      this.internalPage = stockableIds.length ? stockableIds[0] : 0
    },
    originalRequest() {
      // Le parent remplace toujours originalRequest par une référence
      // fraîche après une soumission — que ce soit après succès, ou pour
      // proposer une allocation de stock à valider (la modale reste
      // ouverte dans ce cas, isModalOpen ne change pas). Dans les deux cas
      // la soumission précédente est terminée : un nouveau submit() doit
      // redevenir possible.
      this.resetSubmitting()
    }
  },
  methods: {
    ...mapActions({
      processFetchDraftSupplierOrdersBySupplierIdAction:
        'supplierOrder/fetchDraftSupplierOrdersBySupplierId'
    }),
    ensureDraftOrdersFetched(supplierId) {
      if (!supplierId || this.draftOrdersBySupplier[supplierId] !== undefined) {
        return
      }
      // null = fetch en cours (distinct de undefined = jamais demandé,
      // et d'un tableau = déjà résolu, même vide)
      this.draftOrdersBySupplier[supplierId] = null
      const generation = this.draftOrdersGeneration
      this.processFetchDraftSupplierOrdersBySupplierIdAction({
        supplier_id: supplierId,
        dossier_id: this.localDossier?.id
      })
        .then((response) => {
          // La modale a été fermée (et donc le cache invalidé) pendant ce fetch :
          // on n'écrase pas un cache déjà réinitialisé avec une réponse obsolète.
          if (generation !== this.draftOrdersGeneration) return
          const tempOrdersAddedMeanwhile = (
            Array.isArray(this.draftOrdersBySupplier[supplierId])
              ? this.draftOrdersBySupplier[supplierId]
              : []
          ).filter((order) => order?.id?.toString().startsWith('temp-'))
          this.draftOrdersBySupplier[supplierId] = [
            ...(response.supplier_orders ?? []),
            ...tempOrdersAddedMeanwhile
          ]
        })
        .catch((reason) => {
          console.error('ensureDraftOrdersFetched', reason)
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
          if (generation !== this.draftOrdersGeneration) return
          // undefined = jamais demandé : permet de retenter au prochain accès
          this.draftOrdersBySupplier[supplierId] = undefined
        })
    },
    addTempDraftOrder(supplierId, order) {
      if (!supplierId || !order) return
      const current = Array.isArray(this.draftOrdersBySupplier[supplierId])
        ? this.draftOrdersBySupplier[supplierId]
        : []
      const index = current.findIndex((existing) => existing.id === order.id)
      if (index !== -1) {
        if (current[index] === order) return
        this.draftOrdersBySupplier[supplierId] = [
          ...current.slice(0, index),
          order,
          ...current.slice(index + 1)
        ]
        return
      }
      this.draftOrdersBySupplier[supplierId] = [...current, order]
    },
    removeTempDraftOrder(supplierId, orderId) {
      if (!supplierId || !orderId) return
      const current = this.draftOrdersBySupplier[supplierId]
      if (!Array.isArray(current)) return
      this.draftOrdersBySupplier[supplierId] = current.filter(
        (order) => order.id !== orderId
      )
    },
    resetSubmitting() {
      this.submitting = false
      if (this.submitResetTimeoutId) {
        clearTimeout(this.submitResetTimeoutId)
        this.submitResetTimeoutId = null
      }
    },
    submit() {
      if (this.submitting) return
      this.submitting = true
      // Ce composant n'est pas propriétaire de l'appel réseau (il émet un
      // événement, le parent fait le POST/PUT) : il n'est donc pas informé
      // d'un échec silencieux (ex. handleUpdateItemAfterSelectStock n'a pas
      // de .catch()). Filet de sécurité pour ne pas bloquer indéfiniment le
      // bouton dans ce cas — le cas normal (succès, ou proposition
      // d'allocation de stock à valider) est déjà couvert par les watchers
      // isModalOpen/originalRequest, bien plus rapides que ce délai.
      this.submitResetTimeoutId = setTimeout(() => {
        this.submitting = false
        this.submitResetTimeoutId = null
      }, SUBMIT_SAFETY_TIMEOUT_MS)
      // supplier_order_id peut contenir un identifiant temporaire "temp-"
      // (brouillon de commande pas encore créé côté back, utilisé pour la
      // sélection/le rattachement entre composants d'une même famille). Le
      // back valide ce champ comme un id de commande existant : on n'envoie
      // jamais ce placeholder, le back crée la commande à partir de
      // new_supplier_order (dont l'id temporaire, lui, sert à regrouper les
      // articles qui doivent partager la même commande nouvellement créée).
      const payload = Object.values(this.originalRequest ?? []).map((entry) => {
        // `supplier_orders` (l'historique complet des BDC brouillon du
        // fournisseur, utilisé uniquement pour alimenter le sélecteur dans
        // cette modale) n'est lu nulle part côté back sur ce endpoint — le
        // renvoyer gonfle le payload au point de dépasser post_max_size sur
        // un article dont le fournisseur a de nombreuses commandes (BBSW-9257).
        const rest = { ...entry }
        delete rest.supplier_orders
        if (rest?.supplier_order_id?.toString().startsWith('temp-')) {
          return { ...rest, supplier_order_id: null }
        }
        return rest
      })
      if (this.updateItem) {
        this.$emit('update-item-dossier', payload)
      } else {
        this.$emit('selected-items', {
          type: 'item',
          selectedItem: payload
        })
      }
    }
  }
}
</script>
